import { usePageTitle } from '../hooks/usePageTitle.js';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, type AuditLogRow, type UserRow } from '../lib/api.js';

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function actionColor(action: string): string {
  if (action.includes('login')) return 'var(--accent-green)';
  if (action.includes('logout')) return 'var(--text-dimmed)';
  if (action.includes('rejected')) return 'var(--accent-red)';
  if (action.includes('delete')) return 'var(--accent-red)';
  if (action.includes('update') || action.includes('create')) return 'var(--accent-blue)';
  return 'var(--text-secondary)';
}

export function UserDetailPage({ userId }: { userId: string }) {
  usePageTitle('User Detail');
  const { data, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => api.getUser(userId),
  });

  const { data: logsData } = useQuery({
    queryKey: ['audit-logs', userId],
    queryFn: () => api.listAuditLogs({ userId, limit: 100 }),
  });

  if (isLoading) {
    return <div className="p-8 text-sm text-text-dimmed">Loading…</div>;
  }

  const user = (data as { user: UserRow } | undefined)?.user;
  if (!user) {
    return <div className="p-8 text-sm text-text-dimmed">User not found.</div>;
  }

  const logs: AuditLogRow[] = logsData?.logs ?? [];
  const loginLogs = logs.filter((l) => l.action === 'auth.login');
  const lastLogin = loginLogs[0];

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 items-center gap-3 border-b border-border px-6 shrink-0">
        <Link to="/settings" className="text-xs text-text-dimmed hover:text-text-secondary">
          ← Settings
        </Link>
        <span className="text-text-dimmed">/</span>
        <div className="text-sm font-semibold text-text-primary">{user.displayName}</div>
      </header>

      <section className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* User info card */}
        <div
          className="glass rounded-2xl p-6"
          style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
        >
          <div className="flex items-start gap-5">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
            >
              {user.displayName[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xl font-bold text-text-primary">{user.displayName}</div>
              <div className="font-mono text-sm text-text-secondary">{user.email}</div>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <span
                  className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
                  style={{
                    background: user.role === 'admin' ? 'var(--accent-purple)22' : user.role === 'operator' ? 'var(--accent-blue)22' : 'var(--bg-tertiary)',
                    color: user.role === 'admin' ? 'var(--accent-purple)' : user.role === 'operator' ? 'var(--accent-blue)' : 'var(--text-secondary)',
                    border: `1px solid ${user.role === 'admin' ? 'var(--accent-purple)44' : user.role === 'operator' ? 'var(--accent-blue)44' : 'var(--border)'}`,
                  }}
                >
                  {user.role}
                </span>
                <span
                  className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                  style={{
                    background: user.isActive ? 'var(--accent-green)22' : 'var(--accent-red)22',
                    color: user.isActive ? 'var(--accent-green)' : 'var(--accent-red)',
                    border: `1px solid ${user.isActive ? 'var(--accent-green)44' : 'var(--accent-red)44'}`,
                  }}
                >
                  {user.isActive ? 'active' : 'inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-border grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-text-dimmed">Member since</div>
              <div className="text-sm font-medium text-text-primary mt-1">
                {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-text-dimmed">Last login</div>
              <div className="text-sm font-medium text-text-primary mt-1">
                {user.lastLoginAt
                  ? new Date(user.lastLoginAt).toLocaleString()
                  : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-text-dimmed">Total logins</div>
              <div className="text-sm font-medium text-text-primary mt-1">{loginLogs.length}</div>
            </div>
          </div>
        </div>

        {/* Audit log */}
        <div
          className="glass rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
        >
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div className="text-sm font-semibold text-text-primary">Activity Log</div>
            <div className="text-xs text-text-dimmed">{logs.length} events</div>
          </div>

          {logs.length === 0 ? (
            <div className="p-8 text-center text-sm text-text-dimmed">No activity recorded yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Action</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Resource</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">IP</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">When</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-border last:border-0 hover:bg-bg-tertiary transition-colors duration-100">
                    <td className="px-5 py-3">
                      <span
                        className="font-mono text-xs font-medium"
                        style={{ color: actionColor(log.action) }}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-text-secondary">
                      {log.resource ? (
                        <span>{log.resource}{log.resourceId ? ` · ${log.resourceId.slice(0, 8)}` : ''}</span>
                      ) : (
                        <span className="text-text-dimmed">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-text-dimmed">
                      {log.ip ?? '—'}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-text-dimmed" title={new Date(log.createdAt).toLocaleString()}>
                      {timeAgo(log.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
